// The one panel. Plan new work in it, or change existing work in it — same fields, same layout.
//
// ── WHY IT IS ONE PANEL AND NOT TWO SCREENS ─────────────────────────────────────
// "Nieuwe opdracht plannen … zonder meerdere schermen." A planner deciding to put a job on Tuesday
// does not think in a wizard: werf, fase, taak, uren, wie, opmerking, klaar. Everything is on one
// surface and Opslaan is the only step. Editing is the same surface with the fields filled in, so
// there is one thing to learn instead of two.
//
// ── WHY IT IS SEVERAL WRITES AND SAYS SO ────────────────────────────────────────
// The work itself, its window, who does it, and its status are four authorities in the backend, each
// with its own permission, audit entry and refusal. No endpoint does them together. The panel applies
// only what you changed, in a fixed order, and on a refusal it names what already happened — a Save
// button that half-applied in silence would be worse than four buttons.
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as A from '../api/planning.ts';
import { ConflictError, ForbiddenError, UnauthorizedError } from '../api/client.ts';
import { correlationId, invalidatePlanning, keys } from '../lib/mutate.ts';
import { addDays, daysBetween, longLabel, workdaysBetween, type Ymd } from '../lib/dates.ts';
import { Banner } from '../ui/bits.tsx';
import { History } from '../ui/History.tsx';
import { HourSplit } from '../ui/HourSplit.tsx';
// THE SHAPE LIVES IN lib/panel.ts, re-exported here so every existing import keeps working.
// See that file for why a type shared with lib/commands.ts cannot live in a .tsx.
import type { Assignee, PanelSubject } from '../lib/panel.ts';
export type { Assignee, PanelSubject };



/**
 * THE HOUR PRESETS ALEX ASKED FOR, and a free field beside them.
 *
 * "Niet meer altijd 6 uur." These six cover nearly every real job; the number input covers the rest,
 * so the presets are a shortcut and never a limit.
 */
const HOUR_PRESETS = [2, 4, 6, 8, 12, 16];
const DAY_PRESETS = [1, 2, 3, 5, 10];
/** The order work actually moves through, used to sort the legal moves into something readable. */
const ORDER = ['draft', 'ready', 'planned', 'in_progress', 'blocked', 'completed', 'waived', 'cancelled'];

export function WorkpackagePanel({ subject, people, projects, crew: crewList, onClose }: {
  subject: PanelSubject;
  people: A.Person[];
  projects: A.PlannerProject[];
  /** Deployment facts, so the picker can say what somebody does and the save can order its writes. */
  crew: A.CrewMember[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const creating = subject.workpackageId === null;
  const crewInfo = useMemo(() => new Map(crewList.map((c) => [c.actorId, c])), [crewList]);

  const [projectId, setProjectId] = useState<number | null>(subject.projectId);
  const [phaseId, setPhaseId] = useState<number | null>(subject.projectPhaseId ?? null);
  const [disciplineId, setDisciplineId] = useState<string>('');
  const [title, setTitle] = useState(subject.title);
  const [start, setStart] = useState<Ymd | ''>(subject.plannedStart ?? '');
  const [end, setEnd] = useState<Ymd | ''>(subject.plannedEnd ?? '');
  const [note, setNote] = useState(subject.scope ?? '');
  const [state, setState] = useState(subject.lifecycleState);
  const [category, setCategory] = useState<string>(subject.category ?? '');
  const [showHistory, setShowHistory] = useState(false);
  const [crew, setCrew] = useState<Assignee[]>(subject.assignees);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setProjectId(subject.projectId); setPhaseId(subject.projectPhaseId ?? null);
    setDisciplineId(''); setTitle(subject.title);
    setStart(subject.plannedStart ?? ''); setEnd(subject.plannedEnd ?? '');
    setNote(subject.scope ?? ''); setState(subject.lifecycleState);
    setCategory(subject.category ?? ''); setShowHistory(false);
    setCrew(subject.assignees); setError(null);
  }, [subject]);

  const phases = useQuery({
    queryKey: keys.phases(projectId ?? -1), queryFn: () => A.getPhases(projectId!),
    enabled: projectId !== null, staleTime: 60_000,
  });
  const disciplines = useQuery({
    queryKey: ['disciplines', projectId], queryFn: () => A.getDisciplines(projectId!),
    enabled: projectId !== null, staleTime: 10 * 60_000,
  });
  const lifecycle = useQuery({
    queryKey: ['lifecycle', projectId], queryFn: () => A.getLifecycle(projectId!),
    enabled: projectId !== null && !creating, staleTime: 10 * 60_000,
  });

  // When the werf changes on a NEW job, the phase of the old one means nothing.
  useEffect(() => {
    if (!creating) return;
    setPhaseId(null);
  }, [projectId, creating]);

  /**
   * RESOLVE THE DISCIPLINE FROM ITS CODE, once the list arrives.
   *
   * The schedule carries `disciplineCode` and not the id — the id is a uuid nobody reads and adding it
   * to the read model would have been a migration for a value the disciplines list already holds. So
   * the select is empty for one render and then settles on the right row, rather than the app carrying
   * a second copy of the same mapping.
   */
  useEffect(() => {
    if (disciplineId || !subject.disciplineCode) return;
    const hit = (disciplines.data ?? []).find((d) => d.code === subject.disciplineCode);
    if (hit) setDisciplineId(hit.id);
  }, [disciplines.data, subject.disciplineCode, disciplineId]);

  const reachable = useMemo(() => (lifecycle.data ?? [])
    .filter((e) => e.from_state === subject.lifecycleState)
    // `superseded` is reached by REPLACING a workpackage, never by picking it from a list — that
    // endpoint takes the id of the replacement, which this panel has no way to know.
    .map((e) => e.to_state).filter((s) => s !== 'superseded')
    .sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b)), [lifecycle.data, subject.lifecycleState]);

  const originalDisciplineId = useMemo(
    () => (disciplines.data ?? []).find((d) => d.code === subject.disciplineCode)?.id ?? "",
    [disciplines.data, subject.disciplineCode]);
  const assignable = useMemo(() => people.filter((p) => p.resourceId !== null), [people]);
  const werf = projects.find((p) => p.projectId === projectId);
  const label = werf ? A.werfLabel(werf) : { name: subject.projectName ?? subject.projectCode ?? 'Nieuwe opdracht', code: null };

  const totalHours = crew.reduce((s, c) => s + c.hours, 0);
  const workdays = start && end ? workdaysBetween(start, end) : 0;
  /**
   * The sentence whose absence made "960 tegen 480" unexplainable.
   *
   * The backend spreads each assignment over the WORKING days its window contains, so hours and
   * window are not independent and nothing ever said so. Shown per person, because that is who runs
   * out of day — a two-man job of 16 hours over one day is eight hours each, not sixteen.
   */
  const perPersonPerDay = workdays > 0 && crew.length > 0
    ? Math.max(...crew.map((c) => c.hours / workdays)) : 0;

  const humanError = (e: unknown, what: string): string => {
    if (e instanceof ConflictError) return `${what}: iemand anders heeft dit ondertussen gewijzigd. Sluit het paneel en probeer opnieuw.`;
    if (e instanceof UnauthorizedError) return 'Je sessie is verlopen. Meld je opnieuw aan.';
    if (e instanceof ForbiddenError) return `${what}: je hebt hier geen rechten voor.`;
    return `${what}: ${(e as Error).message}`;
  };

  const setCrewHours = (resourceId: number, hours: number) =>
    setCrew((c) => c.map((x) => (x.resourceId === resourceId ? { ...x, hours } : x)));
  const addPerson = (resourceId: number) =>
    setCrew((c) => (c.some((x) => x.resourceId === resourceId) ? c
      : [...c, { resourceId, hours: c[0]?.hours ?? 8, assignmentId: null }]));
  const removePerson = (resourceId: number) =>
    setCrew((c) => c.filter((x) => x.resourceId !== resourceId));

  const save = async () => {
    setBusy(true); setError(null);
    const applied: string[] = [];
    try {
      if (projectId === null) throw new Error('kies eerst een werf');
      if (!title.trim()) throw new Error('geef de taak een naam');
      if (!start || !end) throw new Error('vul een begin- en einddatum in');
      if (daysBetween(start, end) < 0) throw new Error('de einddatum ligt vóór de begindatum');

      let wpId = subject.workpackageId;
      let revision = subject.planRevision;

      // 1. THE WORK ITSELF. Creating it first means everything after this has something to attach to;
      //    on an edit, only the fields that changed are sent.
      if (creating) {
        if (phaseId === null) throw new Error('kies een fase');
        if (!disciplineId) throw new Error('kies een discipline');
        const made = await A.createWorkpackage(projectId, {
          projectPhaseId: phaseId, disciplineId, title: title.trim(),
          scope: note.trim() || null, correlationId: correlationId(),
        });
        wpId = made.id; revision = 0;
        applied.push('taak aangemaakt');
      } else {
        const patch: { title?: string; scope?: string | null; disciplineId?: string; category?: string } = {};
        if (title.trim() !== subject.title) patch.title = title.trim();
        if ((note.trim() || null) !== (subject.scope ?? null)) patch.scope = note.trim() || null;
        if (disciplineId && disciplineId !== originalDisciplineId) patch.disciplineId = disciplineId;
        // '' is a REAL VALUE here — it is how "— niet gezegd —" clears a category that was set — so
        // the comparison is against the original rather than a truthiness check.
        if (category !== (subject.category ?? '')) patch.category = category;
        if (Object.keys(patch).length > 0) {
          await A.editWorkpackage(projectId, wpId!, { ...patch, correlationId: correlationId() });
          applied.push(Object.keys(patch).length === 1 && patch.scope !== undefined ? 'opmerking' : 'taakgegevens');
          if (patch.category !== undefined) {
            A.recordEvent({
              eventType: 'task_category_changed', projectId, workpackageId: wpId!,
              valueBefore: { category: subject.category ?? null }, valueAfter: { category: patch.category || null },
              source: 'manual', screen: 'project',
            });
          }
          if (patch.title !== undefined) {
            A.recordEvent({
              eventType: 'workpackage_renamed', projectId, workpackageId: wpId!,
              valueBefore: { title: subject.title }, valueAfter: { title: patch.title },
              source: 'manual', screen: 'project',
            });
          }
        }
      }

      // 2. THE WINDOW, before the hours: the hours are spread over it, so setting them against a
      //    window that is about to change computes the daily load twice.
      const windowChanged = creating
        || (start || null) !== subject.plannedStart || (end || null) !== subject.plannedEnd;
      if (windowChanged) {
        await A.setWorkpackageWindow(projectId, wpId!, {
          start, end, reason: 'gewijzigd in het planpaneel',
          expectedRevision: revision, correlationId: correlationId(),
        });
        applied.push('planning');
      }

      // 3. THE CREW. Person and hours live on the assignment, and `uq_wa_active_pair` leaves no route
      //    but supersede-then-assign, so a changed number of hours is a withdrawal and a new booking.
      //
      // ── ADD BEFORE YOU REMOVE, AND PUT THE SUPERVISOR IN FIRST ──────────────────
      // This ran removals-then-additions, which is the natural way to write it and the wrong way to
      // execute it. Two things refuse an assignment on its merits now: somebody who is not booked
      // alone arriving without a supervisor, and a supervisor being withdrawn from under somebody who
      // still needs one. Under the old order, swapping a duo in one edit emptied the job first and
      // then hit the refusal — leaving real work on a real werf with nobody on it.
      //
      // Three passes, in this order, and each one is safe to stop at:
      //   1. NEW people, supervisors first — so a never-solo colleague never lands on an empty job.
      //   2. CHANGED hours — necessarily withdraw-then-assign, same pair, no way around it.
      //   3. REMOVALS last — the only step that can leave somebody unsupervised, so the trigger's
      //      refusal costs nothing that came before it.
      const before = new Map(subject.assignees.map((a) => [a.resourceId, a]));
      const after = new Map(crew.map((a) => [a.resourceId, a]));
      const changedHours = (rid: number, a: Assignee) => {
        const was = before.get(rid);
        return was !== undefined && Math.abs(was.hours - a.hours) > 0.01;
      };
      const byActor = new Map(people.filter((p) => p.resourceId !== null).map((p) => [p.resourceId!, p.actorId]));
      const maySupervise = (rid: number) => !!crewInfo.get(byActor.get(rid) ?? '')?.maySupervise;

      const additions = [...after].filter(([rid]) => !before.has(rid))
        // Supervisors first. Not cosmetic: the check runs per INSERT, so a never-solo person inserted
        // before the person who supervises them is refused on a job that would have been legal one
        // statement later.
        .sort(([a], [b]) => Number(maySupervise(b)) - Number(maySupervise(a)));
      for (const [rid, a] of additions) {
        await A.assign(projectId, {
          workpackageId: wpId!, resourceId: rid, allocatedHours: a.hours,
          reason: 'toegewezen in het planpaneel', correlationId: correlationId(),
        });
      }
      for (const [rid, a] of after) {
        if (!changedHours(rid, a)) continue;
        const was = before.get(rid)!;
        if (was.assignmentId !== null) await A.unassign(projectId, was.assignmentId, correlationId());
        await A.assign(projectId, {
          workpackageId: wpId!, resourceId: rid, allocatedHours: a.hours,
          reason: 'uren gewijzigd in het planpaneel', correlationId: correlationId(),
        });
      }
      for (const [rid, a] of before) {
        if (after.has(rid)) continue;
        if (a.assignmentId !== null) await A.unassign(projectId, a.assignmentId, correlationId());
      }
      if (before.size !== after.size || [...after].some(([r, a]) => (before.get(r)?.hours ?? -1) !== a.hours)) {
        applied.push('medewerkers');
      }

      // 4. STATUS LAST. Its refusal ("an approver must close this") is a statement about the work
      //    rather than about the plan, and losing the other three to it would be the wrong trade.
      if (!creating && state !== subject.lifecycleState) {
        await A.transitionWorkpackage(projectId, wpId!, {
          toState: state, reason: 'gewijzigd in het planpaneel', correlationId: correlationId(),
        });
        applied.push('status');
      }

      invalidatePlanning(qc, projectId);
      qc.invalidateQueries({ queryKey: keys.projects });
      onClose();
    } catch (e) {
      // WHAT ALREADY HAPPENED IS NAMED. These are separate writes and the earlier ones are not undone;
      // "opslaan mislukt" would leave a planner believing nothing changed while their job had moved.
      setError(humanError(e, applied.length ? `${applied.join(' + ')} opgeslagen, daarna` : 'Niet opgeslagen'));
      if (projectId !== null) invalidatePlanning(qc, projectId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="scrim" onClick={busy ? undefined : onClose} aria-hidden />
      <aside className="panel" role="dialog" aria-modal="true"
             aria-label={creating ? 'Nieuwe opdracht plannen' : `Werkpakket ${subject.title}`}>
        <header className="panel-head">
          <div style={{ minWidth: 0 }}>
            <div className="werf-name">{creating ? 'Nieuwe opdracht' : label.name}</div>
            {!creating && label.code && <div className="werf-code">{label.code}</div>}
          </div>
          <button className="btn small" onClick={onClose} aria-label="Sluiten">✕</button>
        </header>

        <div className="panel-body stack">
          {error && <Banner kind="err">{error}</Banner>}

          {/* ── werf · fase · discipline ──────────────────────────────────────── */}
          {creating && (
            <div className="field">
              <label htmlFor="wp-project">Werf</label>
              <select id="wp-project" value={projectId ?? ''}
                      onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">— kies een werf —</option>
                {projects.map((p) => (
                  <option key={p.projectId} value={String(p.projectId)}>
                    {A.werfLabel(p).name}{A.werfLabel(p).code ? ` (${A.werfLabel(p).code})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {creating && (
            <div className="field">
              <label htmlFor="wp-phase">Fase</label>
              <select id="wp-phase" value={phaseId ?? ''} disabled={projectId === null}
                      onChange={(e) => setPhaseId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">{projectId === null ? '— kies eerst een werf —' : '— kies een fase —'}</option>
                {(phases.data ?? []).map((ph) => (
                  <option key={ph.id} value={String(ph.id)}>
                    {ph.templateName ?? `Fase ${ph.id}`}
                    {ph.plannedStart ? ` (${ph.plannedStart} → ${ph.plannedEnd})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="field">
            <label htmlFor="wp-title">Taak</label>
            <input id="wp-title" value={title} maxLength={300} placeholder="Bv. vloerverwarming plaatsen"
                   onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="wp-discipline">Discipline</label>
            <select id="wp-discipline" value={disciplineId} disabled={projectId === null}
                    onChange={(e) => setDisciplineId(e.target.value)}>
              <option value="">— kies een discipline —</option>
              {(disciplines.data ?? []).filter((d) => d.active || d.id === originalDisciplineId)
                .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {/* ── wanneer ───────────────────────────────────────────────────────── */}
          <div className="two-up">
            <div className="field">
              <label htmlFor="wp-start">Van</label>
              <input id="wp-start" type="date" value={start}
                     onChange={(e) => {
                       const v = e.target.value as Ymd;
                       // Keep the length when the start moves — that is what dragging does, and the
                       // panel must not quietly behave differently from the gesture beside it.
                       if (start && end && v) setEnd(addDays(v, daysBetween(start, end)));
                       setStart(v);
                     }} />
            </div>
            <div className="field">
              <label htmlFor="wp-end">Tot en met</label>
              <input id="wp-end" type="date" value={end} min={start || undefined}
                     onChange={(e) => setEnd(e.target.value as Ymd)} />
            </div>
          </div>
          <div className="chips">
            {DAY_PRESETS.map((d) => (
              <button key={d} type="button" className={`btn small${workdays === d ? ' primary' : ''}`}
                      disabled={!start}
                      onClick={() => { if (start) setEnd(addWorkdaysYmd(start, d)); }}>
                {d} {d === 1 ? 'werkdag' : 'werkdagen'}
              </button>
            ))}
          </div>

          {/* ── wat voor werk is dit ──────────────────────────────────────────
              NOT the discipline and not the phase. "Welke soort uren zijn dit" is the axis a planner
              sorts by when deciding who does what — and the axis any later question about who usually
              does the werfopvolging has to group on. Optional: an unset category never blocks a save. */}
          <div className="field">
            <label htmlFor="soort">Soort werk</label>
            <select id="soort" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">— niet gezegd —</option>
              {A.CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* ── wie, en hoeveel uur ───────────────────────────────────────────── */}
          <div className="field">
            <label>Medewerkers</label>
            {crew.length === 0 && <span className="faint">Nog niemand toegewezen.</span>}
            {crew.map((c) => {
              const p = assignable.find((x) => x.resourceId === c.resourceId);
              const info = p ? crewInfo.get(p.actorId) : undefined;
              return (
                <div key={c.resourceId} className="crew-row">
                  <span className="crew-name">
                    {p?.displayName ?? `#${c.resourceId}`}
                    {/* A FACT, NOT A VERDICT. Whether this particular crew satisfies the rule is the
                        server's answer and only the server's — re-deriving it here would be the same
                        rule written twice, and the copy is the one that goes stale. What the panel
                        shows is what the register publishes about the person, so a planner can see
                        the duo coming before they hit Opslaan. */}
                    {info?.needsSupervision && <span className="badge duo" title="Wordt niet alleen ingepland">werkt in duo</span>}
                  </span>
                  <div className="chips">
                    {HOUR_PRESETS.map((h) => (
                      <button key={h} type="button" className={`btn small${c.hours === h ? ' primary' : ''}`}
                              onClick={() => setCrewHours(c.resourceId, h)}>{h}u</button>
                    ))}
                  </div>
                  <input type="number" min={0} max={400} step={0.5} value={c.hours} aria-label="uren"
                         onChange={(e) => setCrewHours(c.resourceId, Math.max(0, Number(e.target.value)))} />
                  <button type="button" className="btn small" aria-label="Verwijderen"
                          onClick={() => removePerson(c.resourceId)}>✕</button>
                </div>
              );
            })}
            <select value="" aria-label="Medewerker toevoegen"
                    onChange={(e) => { if (e.target.value) addPerson(Number(e.target.value)); }}>
              <option value="">+ medewerker toevoegen…</option>
              {/* THE TRADES ARE IN THE OPTION, because "wie kan dit" is the question being asked at the
                  moment this dropdown opens. Only trades the register publishes; a person with none
                  listed shows their name alone rather than an empty bracket. */}
              {assignable.filter((p) => !crew.some((c) => c.resourceId === p.resourceId))
                .map((p) => {
                  const info = crewInfo.get(p.actorId);
                  const t = A.tradeSummary(info?.skills ?? [], 4);
                  return (
                    <option key={p.actorId} value={String(p.resourceId)}>
                      {p.displayName}
                      {t.shown ? ` — ${t.shown}${t.hidden > 0 ? ` +${t.hidden}` : ''}` : ''}
                      {info?.needsSupervision ? ' (werkt in duo)' : ''}
                    </option>
                  );
                })}
            </select>
          </div>

          {start && end && crew.length > 0 && (
            <p className={perPersonPerDay > 8 ? 'banner warn' : 'faint'} style={{ margin: 0 }}>
              {totalHours} u over {workdays} werkdag{workdays === 1 ? '' : 'en'}
              {crew.length > 1 ? ` met ${crew.length} mensen` : ''} ={' '}
              <strong>{perPersonPerDay.toFixed(1).replace('.', ',')} u per dag per persoon</strong>
              {perPersonPerDay > 8 && ' — meer dan een werkdag. Verleng het venster, verlaag de uren of zet er iemand bij.'}
              {workdays === 0 && ' — dit venster bevat geen enkele werkdag.'}
            </p>
          )}

          {/* ── uren verdelen ─────────────────────────────────────────────────
              The days this work actually lands on. Only for a task that exists: a workpackage being
              created has no assignments yet, so there is nothing to spread hours over until it is
              saved once. */}
          {!creating && projectId !== null && subject.workpackageId !== null && (
            <HourSplit projectId={projectId} workpackageId={subject.workpackageId} people={people} />
          )}

          {/* ── wat er met deze taak gebeurd is ───────────────────────────────
              Behind a disclosure: it is the answer to "waarom staat dit zo", which is a question you
              ask occasionally, not a field you fill in. Renders nothing at all for anybody without
              `planner_events.read` — see ui/History.tsx for why that is silence and not an error. */}
          {!creating && projectId !== null && subject.workpackageId !== null && (
            <div className="field">
              <button type="button" className="btn small" onClick={() => setShowHistory((v) => !v)}
                      aria-expanded={showHistory}>
                {showHistory ? 'Historie verbergen' : 'Historie tonen'}
              </button>
              {showHistory && <History projectId={projectId} workpackageId={subject.workpackageId} />}
            </div>
          )}

          {/* ── status ────────────────────────────────────────────────────────── */}
          {!creating && (
            <div className="field">
              <label htmlFor="wp-state">Status</label>
              <select id="wp-state" value={state} onChange={(e) => setState(e.target.value)}>
                {/* ONLY THE MOVES THE DATABASE ALLOWS FROM HERE, plus staying put. */}
                <option value={subject.lifecycleState}>{A.lifecycleLabel(subject.lifecycleState)}</option>
                {reachable.map((s) => <option key={s} value={s}>{A.lifecycleLabel(s)}</option>)}
              </select>
            </div>
          )}

          <div className="field">
            <label htmlFor="wp-note">Opmerkingen</label>
            <textarea id="wp-note" rows={3} value={note} maxLength={4000}
                      placeholder="Bv. klant is vrijdag niet thuis, materiaal ligt al op de werf…"
                      onChange={(e) => setNote(e.target.value)} />
          </div>

          {!creating && subject.plannedStart && (
            <p className="faint" style={{ margin: 0 }}>
              Nu ingepland: {longLabel(subject.plannedStart)}
              {subject.plannedEnd && subject.plannedEnd !== subject.plannedStart ? ` t/m ${longLabel(subject.plannedEnd)}` : ''}.
            </p>
          )}
        </div>

        <footer className="panel-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Annuleren</button>
          <button className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Opslaan…' : creating ? 'Inplannen' : 'Opslaan'}
          </button>
        </footer>
      </aside>
    </>
  );
}

/** `n` working days starting at `from` inclusive, as the end date. Weekends do not count. */
function addWorkdaysYmd(from: Ymd, n: number): Ymd {
  let d = from, left = n - 1;
  while (left > 0) {
    d = addDays(d, 1);
    const g = new Date(`${d}T12:00:00`).getDay();
    if (g !== 0 && g !== 6) left--;
  }
  return d;
}
